/**
 * WHAT A CLEANER SEES. Her own visits for the next two weeks, and nothing that is not hers.
 *
 * THE SERVER DECIDES WHAT "HERS" MEANS. GET /api/visits goes through the scoped reader in
 * `api/src/db/scoped.ts`, which narrows to her live assignments and leaves the price column out of
 * the select altogether. This screen could not show a price if it tried, and that is the design:
 * CLAUDE.md binding rule 6, the interface arranges and the server enforces.
 *
 * A visit here is a day, a window and who she is working with, and ONE GOLD BUTTON that opens the
 * job sheet. The card is not a link any more: a whole-card tap target hid the destination, and the
 * one thing she wants from this screen is worth naming in words she can read at arm's length.
 *
 * The same reader is why there is no household name and no town on a card. `crewVisitColumns` is
 * fourteen columns of the visit and nothing joined to it, so the address reaches her on the sheet,
 * where the audit trail for reading it already exists, and not on a list she scrolls past.
 *
 * And what will never land here: anything that records where she is. Start and finish exist to
 * check the rate card and protect her pay. The brief names surveillance as a thing this must not
 * become, and the way that is written in a design is a column that does not exist.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { VISIT_KIND_LABELS } from '@fk/shared';
import { ApiError, listVisits, type Me, type VisitWithCrew } from '../api.ts';
import { Card, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { Busy, EmptyNotice, ErrorNotice } from '../components/Notice.tsx';
import { VisitStatusPill } from '../components/StatusPills.tsx';
import { crewNames } from '../components/VisitBits.tsx';
import { addDays, formatDay, formatWindow, groupByDay, shortDay } from '../format.ts';

const DAYS_AHEAD = 14;

export function MyWork({ me }: { me: Me }) {
  const [rows, setRows] = useState<readonly VisitWithCrew[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setError(null);
    listVisits(me.today, addDays(me.today, DAYS_AHEAD))
      .then((answer) => {
        if (live) setRows(answer.visits);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'Your visits did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [me.today, attempt]);

  return (
    <div>
      <ScreenTitle title={`Good morning, ${me.displayName}`} subtitle={formatDay(me.today) ?? ''} />

      {error ? (
        <ErrorNotice title="Your visits did not load" message={error.message} onRetry={reload} />
      ) : rows === null ? (
        <Busy what="your visits" />
      ) : rows.length === 0 ? (
        <EmptyNotice title="Nothing on your calendar yet">
          <p>
            When the office puts you on a visit it appears here, with the day and the arrival
            window.
          </p>
        </EmptyNotice>
      ) : (
        groupByDay(rows).map(([day, visits]) => {
          const near = day === me.today || day === addDays(me.today, 1);
          return (
            <section key={day} className="mb-2.5">
              <h2 className="mb-2 font-serif text-xl leading-[26px]">
                {near ? (day === me.today ? 'Today' : 'Tomorrow') : shortDay(day)}
                {near ? (
                  <span className="ml-2 text-[15px] text-navy/60">{shortDay(day)}</span>
                ) : null}
              </h2>
              <ul className="flex flex-col gap-2.5">
                {visits.map((visit) => {
                  const window = formatWindow(visit.windowStart, visit.windowEnd);
                  const others = crewNames(
                    visit.cleaners.filter((cleaner) => cleaner.cleanerId !== me.cleanerId),
                  );
                  return (
                    <li key={visit.id}>
                      <Card
                        className={`flex flex-col gap-1.5 py-3 ${
                          visit.status === 'cancelled' ? 'opacity-70' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-lg leading-snug font-semibold">
                            {window ?? 'No arrival window set'}
                          </h3>
                          <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
                            {visit.status === 'planned' ? null : (
                              <VisitStatusPill status={visit.status} />
                            )}
                            <Pill>{VISIT_KIND_LABELS[visit.kind]}</Pill>
                          </div>
                        </div>
                        <p className="text-sm text-navy/75">
                          {others ? `With ${others}.` : 'Just you on this one.'}
                        </p>
                        <Link
                          to={`/visits/${visit.id}/sheet`}
                          aria-label={`Open the job sheet, ${shortDay(day)}, ${
                            window ?? 'no arrival window set'
                          }`}
                          className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-gold bg-gold px-4 text-base font-semibold text-navy"
                        >
                          Open the job sheet
                          <svg
                            aria-hidden="true"
                            viewBox="0 0 20 20"
                            className="h-4 w-4 shrink-0"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={1.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M8 5l5 5-5 5" />
                          </svg>
                        </Link>
                      </Card>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })
      )}

      <Card className="mt-2.5 py-3">
        <h2 className="font-serif text-lg leading-6">What this will never do</h2>
        <p className="mt-1 text-sm">
          It will not track where you are. Start and finish protect your pay.
        </p>
      </Card>
    </div>
  );
}
